import { Header, Layout1, Sidebar, StackedTitle, useFuzzySearch } from '@ljagis/react-core';
import {
  GeolocationButton,
  Identify,
  Layers,
  LocationSearch,
  Map,
  MapOverlay,
  StyleGallery,
  ZoomInButton,
  ZoomOutButton,
  useGeoJSONLayer,
  useMapFeatureState,
  useMapIdentify,
} from '@ljagis/react-mapping';
import { Box, Divider, IconButton, Tooltip, Typography } from '@material-ui/core';
import IdentifyIcon from '@material-ui/icons/FeaturedPlayListOutlined';
import LayersIcon from '@material-ui/icons/LayersOutlined';
import ListIcon from '@material-ui/icons/ListAlt';
import logo from 'assets/img/logo.png';
import axios from 'axios';
import { TractorZoneList, tractorZoneLayers } from 'components/TractorZones';
import React, { useEffect, useState } from 'react';
import { searchLocation } from 'services/searchLocation';
import { TractorZoneFeature, fetch as fetchTractorZones } from 'services/tractorZones';

const {
  REACT_APP_DITCH_SEARCH_URL: ditchSearchUrl = '',
  REACT_APP_MAPBOX_ACCESS_TOKEN: mapboxAccessToken = '',
} = process.env;

export interface MowingPublicPageProps {
  title: string;
  subtitle: string;
  mapStyles: { title: string; previewImage: string; previewImage2x: string; styleUrl: string }[];
}

const vehicleLocationsLayers = [
  {
    image: {
      id: 'vehicle-locations',
      source:
        'https://ljamaptiles.blob.core.windows.net/jeffersondd6-styles/icons/circle-blue-48/default.png?sv=2019-12-12&si=jeffersondd6-styles-webaccess&sr=c&sig=MaYnlUJjE2rK6gp95A6ULpCRd34IXvRFMjn2DcJeZZ8%3D',
    },
    metadata: {
      'lja:visibility': 'vehicle-locations',
      'lja:legend': 'vehicle-location',
    },
    type: 'symbol' as const,
    layout: {
      'symbol-z-order': 'viewport-y',
      'icon-image': 'vehicle-locations',
      'icon-size': 0.55,
      'icon-allow-overlap': true,
      'text-letter-spacing': 0.1,
      'text-optional': true,
      'text-field': ['get', 'vehicleNumber'] as ['get', string],
      'text-size': 8,
    },
    paint: {
      'text-color': 'white',
    },
  },
];

type sidebars = 'layers' | 'identify' | 'zones';

const MowingPublicPage: React.FC<MowingPublicPageProps> = ({ title, subtitle, mapStyles }) => {
  const [results, setResults] = useState<TractorZoneFeature[]>([]);

  // Used to highlight when selected
  const [selectedZone, setSelectedZone] = useState<TractorZoneFeature | null>(null);

  const { setData: setMapTractorZoneData, sourceId: tractorZoneSource } = useGeoJSONLayer({
    layers: tractorZoneLayers,
  });

  const { setData: setVehicleLocationData } = useGeoJSONLayer({
    layers: vehicleLocationsLayers,
  });

  const {
    layerNames: identifyLayers,
    features: identifyFeatures,
    location: identifyLocation,
    clearFeatures: clearIdentify,
  } = useMapIdentify();

  const { highlightFeature } = useMapFeatureState();

  const { results: fuzzyResults, setQuery } = useFuzzySearch<TractorZoneFeature>({
    list: results,
    options: {
      keys: ['properties.zone', 'properties.location'],
      threshold: 0.2,
    },
  });

  const [sidebar, setSidebar] = useState<sidebars | undefined>('zones');

  const sidebars: Record<
    sidebars,
    { title: string; content: React.ReactNode; enableSearch?: boolean }
  > = {
    layers: {
      title: 'Layers',
      content: (
        <Box flex={1} display="flex" flexDirection="column" minHeight={0}>
          <Layers flex={1} />
          <Divider />
          <StyleGallery items={mapStyles} />
        </Box>
      ),
    },
    identify: {
      title: 'Feature Details',
      content: (
        <Identify
          flex={1}
          layerNames={identifyLayers}
          features={identifyFeatures}
          location={identifyLocation}
          onClearFeatures={clearIdentify}
        />
      ),
    },
    zones: {
      title: 'Tractor Area Cycles',
      enableSearch: true,
      content: <TractorZoneList items={fuzzyResults} onItemSelected={setSelectedZone} />,
    },
  };

  // Fetch project data on first load
  useEffect(() => {
    const getTractorZones = async () => {
      const geoJSON = await fetchTractorZones();
      setResults(geoJSON.features);
    };
    getTractorZones();
  }, [setResults]);

  // Fetch tractor locations on load
  useEffect(() => {
    const getVehicleLocations = async () => {
      const response = await axios.get(
        'https://ex9pwaofdg.execute-api.us-east-1.amazonaws.com/prod/location'
      );
      setVehicleLocationData(response.data);
    };
    getVehicleLocations();
  }, [setVehicleLocationData]);

  // Update map data on any changes to zone search results
  useEffect(() => {
    setMapTractorZoneData({ type: 'FeatureCollection', features: fuzzyResults });
  }, [fuzzyResults, setMapTractorZoneData]);

  // Reset selected project when sidebar changes from projects
  useEffect(() => {
    return () => {
      sidebar === 'zones' && setSelectedZone(null);
    };
  }, [sidebar]);

  // Highlight selected project from list on map
  useEffect(() => {
    if (!selectedZone?.id) {
      return undefined;
    }

    let unhighlight: () => void;

    try {
      unhighlight = highlightFeature({ source: tractorZoneSource, id: selectedZone.id });
    } catch (_err) {
      // NOOP
    }

    return () => {
      try {
        unhighlight && unhighlight();
      } catch (_err) {
        // NOOP
      }
    };
  }, [selectedZone, highlightFeature, tractorZoneSource]);

  // Open identify sidebar on any new identified features
  useEffect(() => {
    if (!identifyFeatures.length) {
      return;
    }
    setSidebar('identify');
  }, [identifyFeatures]);

  return (
    <Layout1
      height={'100vh'}
      header={
        <Header minHeight={64} color="secondary">
          <img src={logo} alt="" style={{ height: 56, width: 56, marginRight: 16 }} />
          <Box flex="1 1 auto">
            <StackedTitle title={title} subtitle={subtitle} />
          </Box>
          <Tooltip title="Projects">
            <IconButton color="inherit" onClick={() => setSidebar('zones')}>
              <ListIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Layers">
            <IconButton color="inherit" onClick={() => setSidebar('layers')}>
              <LayersIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Feature Details">
            <IconButton color="inherit" edge="end" onClick={() => setSidebar('identify')}>
              <IdentifyIcon />
            </IconButton>
          </Tooltip>
        </Header>
      }
      map={<Map style={{ height: '100%' }} initialMapStyle={mapStyles[0].styleUrl} />}
      mapOverlayTopLeft={[
        <MapOverlay key="locationSearch">
          <Box key={1} minWidth={240}>
            <LocationSearch
              placeholder="Search for an address or ditch"
              onSearch={searchLocation(mapboxAccessToken, ditchSearchUrl)}
            />
          </Box>
        </MapOverlay>,
      ]}
      mapOverlayTopRight={[
        <MapOverlay key="navigation">
          <Box>
            <ZoomInButton />
            <Divider />
            <ZoomOutButton />
          </Box>
        </MapOverlay>,
        <MapOverlay key="geolocation">
          <GeolocationButton />
        </MapOverlay>,
      ]}
      mapOverlayBottomLeft={[
        <MapOverlay key="legend">
          <Box width={240} display="flex" flexDirection="column" p={1}>
            <Box display="flex">
              <Box display="flex" width="50%">
                <Box p={1} mr={1} bgcolor="#2BFF00" height={20} width={20}></Box>
                <Typography variant="caption">Completed</Typography>
              </Box>
              <Box display="flex">
                <Box flex={1} p={1} mr={1} bgcolor="#FF9C1A" height={20} width={20}></Box>
                <Typography variant="caption">Scheduled</Typography>
              </Box>
            </Box>
            <Box display="flex" mt={1}>
              <Box display="flex" width="50%">
                <Box p={1} mr={1} bgcolor="fuchsia" height={20} width={20}></Box>
                <Typography variant="caption">In Progress</Typography>
              </Box>
              <Box display="flex">
                <Box flex={1} p={1} mr={1} bgcolor="gray" height={20} width={20}></Box>
                <Typography variant="caption">Not Maintained</Typography>
              </Box>
            </Box>
          </Box>
        </MapOverlay>,
      ]}
      sidebar={
        sidebar && (
          <Sidebar
            height={1}
            title={sidebars[sidebar].title}
            onClose={() => setSidebar(undefined)}
            content={sidebars[sidebar].content}
            enableSearch={sidebars[sidebar].enableSearch}
            onSearch={setQuery}
            onClearSearch={() => setQuery('')}
          />
        )
      }
    />
  );
};

export default MowingPublicPage;
